import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { purchaseAttemptTracker } from 'metrics/user-analytics/purchase';
import LoadingScreen from 'components/LoadingScreen';
import wayIsOpenTicket from 'utils/wayIsOpenTicket';

const propTypes = {
  purchase: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    isExchange: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
    departs: PropTypes.object,
    returns: PropTypes.object,
    bulkTicket: PropTypes.bool,
    roundTrip: PropTypes.bool,
  }).isRequired,
  createPurchase: PropTypes.func.isRequired,
  syncSeatsFromBus: PropTypes.func.isRequired,
  reconcileBfoSeats: PropTypes.func,
  replaceUrl: PropTypes.func.isRequired,
  search: PropTypes.shape({
    departure: PropTypes.shape({
      routeType: PropTypes.string,
    }),
  }).isRequired,
  frequentPassengers: PropTypes.array.isRequired,
  features: PropTypes.object.isRequired,
};

/**
 * NewPurchase component.
 * Handles the creation of a new purchase and redirects based on purchase state and seat selection.
 * @param {object} props - createPurchase, purchase, replaceUrl, search, frequentPassengers, features
 * @param {function} props.createPurchase - Function to initialize a new purchase
 * @param {object} props.purchase - Purchase state containing isFetching, isExchange, token, departs, returns
 * @param {function} props.replaceUrl - Function to handle URL navigation
 * @param {object} props.search - Search state including departure route type
 * @param {array} props.frequentPassengers - Array of frequent passengers
 * @param {object} props.features - Whitelabel features configuration
 */
class NewPurchase extends Component {
  componentDidMount() {
    this.unmounted = false;
    const { createPurchase } = this.props;
    createPurchase();
  }

  componentWillReceiveProps(nextProps) {
    const { replaceUrl, purchase, syncSeatsFromBus } = this.props;
    const { purchase: nextPurchase, frequentPassengers, features } = nextProps;

    if (purchase.isFetching && !nextPurchase.isFetching) {
      // Load seats from seatsBus if they exist

      purchaseAttemptTracker(nextPurchase);
      const redirectUrl = `/purchase/${nextPurchase.token}`;

      syncSeatsFromBus();

      const hasDepartureSeats = Boolean(nextPurchase.departs?.selectedSeats?.length);
      const hasReturnSeats = Boolean(nextPurchase.returns?.selectedSeats?.length);
      const isBulkTicket = nextPurchase.bulkTicket;
      const hasFrequentPassengers =
        Boolean(features.SHOW_FREQUENT_PASSENGERS) && Boolean(frequentPassengers.length);

      /**
       * Determines the redirect path based on the given conditions.
       * @param {boolean} isBulkPurchase - If the purchase is in bulk.
       * @param {boolean} hasDepartureSeats - If departure seats have been selected.
       * @param {boolean} hasReturnSeats - If return seats have been selected.
       * @param {boolean} isRoundTrip - If the purchase is round trip.
       * @returns {string} The redirect path.
       */
      const getRedirectPath = (isBulkPurchase, hasDepartureSeats, hasReturnSeats, isRoundTrip) => {
        if (isBulkPurchase) return 'checkout';

        // If seats are selected, user is redirected to passengers page
        if (!isRoundTrip && hasDepartureSeats) {
          return `passengers?frequentPassengers=${hasFrequentPassengers}`;
        }

        if (isRoundTrip && hasDepartureSeats && hasReturnSeats) {
          return `passengers?frequentPassengers=${hasFrequentPassengers}`;
        }

        // If departure seats are not selected, redirect to departure seat selection
        if (!hasDepartureSeats && !wayIsOpenTicket(nextPurchase.departs)) {
          return `seats/departure?frequentPassengers=${hasFrequentPassengers}`;
        }

        // If departure seats are selected but return seats are not (for round trips)
        if (isRoundTrip && !hasReturnSeats && !wayIsOpenTicket(nextPurchase.returns)) {
          return `seats/return?frequentPassengers=${hasFrequentPassengers}`;
        }

        return `passengers?frequentPassengers=${hasFrequentPassengers}`;
      };

      const redirectPath = getRedirectPath(
        isBulkTicket,
        hasDepartureSeats,
        hasReturnSeats,
        nextPurchase.roundTrip,
      );

      // RM-1201: a canonical BFO seats-on-results purchase must not advance until fresh
      // purchase-scoped availability is fetched and every provisional selection is
      // reconciled. The thunk returns null synchronously for every other purchase, so
      // ordinary journeys keep the existing immediate redirect.
      const { reconcileBfoSeats } = this.props;
      const bfoReconciliation = reconcileBfoSeats ? reconcileBfoSeats() : null;
      if (bfoReconciliation) {
        bfoReconciliation.then((outcome) => {
          if (this.unmounted) return;

          const path =
            outcome === 'reconciled'
              ? redirectPath
              : `seats/departure?frequentPassengers=${hasFrequentPassengers}`;
          replaceUrl(`${redirectUrl}/${path}`);
        });
        return;
      }

      replaceUrl(`${redirectUrl}/${redirectPath}`);
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  render() {
    return <LoadingScreen backgroundColor="white" />;
  }
}

NewPurchase.propTypes = propTypes;

export default NewPurchase;
